/**
 * Paying with Chapa: what the apps and the functions both need to know.
 *
 * The charge runs on NADAWI Technologies' Chapa account, and Chapa splits it
 * straight to the committee's subaccount, keeping the agreed commission. Liyu
 * never holds the money, and no payment hold or release exists to rely on.
 *
 * As with Telebirr, nothing the browser says marks money paid. The function
 * asks Chapa, and only a transaction Chapa reports as a success, in birr, for
 * the amount the order was opened for, becomes a verified payment.
 */

import { assertBirr } from "./money.js";

export const CHAPA_ORDER_STATUSES = ["started", "paid", "needsReview", "failed"] as const;
export type ChapaOrderStatus = (typeof CHAPA_ORDER_STATUSES)[number];

/**
 * Where Chapa sends the resident back to, with `?chapa=<orderId>` added.
 * Fixed here, never taken from a request: the request only says which app the
 * resident started from.
 *
 * Chapa only returns to an https address, so the phone's is a page on the web
 * app that hands the resident on to `liyu://pay` (CHAPA_APP_RETURN).
 */
export const CHAPA_RETURN = {
  web: "https://app.liyumanagement.com/app.html",
  phone: "https://app.liyumanagement.com/chapa-return.html",
} as const;
/** Where that page sends the phone, inside the app. */
export const CHAPA_APP_RETURN = "liyu://pay";
export const CHAPA_RETURNS = ["web", "phone"] as const;
export type ChapaReturn = (typeof CHAPA_RETURNS)[number];

export interface ChapaOrder {
  id: string;
  orgId: string;
  unitId: string;
  termId: string;
  amount: number;
  status: ChapaOrderStatus;
  /** The verified payment it became, once it did. */
  paymentId: string | null;
  createdAt: Date | null;
}

/**
 * A payment reference: letters, digits and a dash, within Chapa's 20
 * characters, unique per order so the same reference is never sent twice.
 * The order document says which complex it belongs to, so the reference
 * need not.
 */
export const CHAPA_REF_LENGTH = 20;
export function chapaTxRef(random: string): string {
  const ref = `lyu-${random.replace(/[^a-zA-Z0-9]/g, "")}`;
  if (ref.length < 12) {
    throw new RangeError("A Chapa reference needs at least 8 random characters");
  }
  return ref.slice(0, CHAPA_REF_LENGTH);
}

export function toChapaOrder(id: string, data: Record<string, unknown>): ChapaOrder {
  const status = CHAPA_ORDER_STATUSES.find((known) => known === data.status) ?? "started";
  const created = data.createdAt as { toDate?: () => Date } | Date | undefined;
  return {
    id,
    orgId: typeof data.orgId === "string" ? data.orgId : "",
    unitId: typeof data.unitId === "string" ? data.unitId : "",
    termId: typeof data.termId === "string" ? data.termId : "",
    amount: typeof data.amount === "number" ? data.amount : 0,
    status,
    paymentId: typeof data.paymentId === "string" ? data.paymentId : null,
    createdAt:
      created instanceof Date
        ? created
        : typeof created?.toDate === "function"
          ? created.toDate()
          : null,
  };
}

/** How long after starting a Chapa order the app still asks Chapa about it. */
export const CHAPA_RECHECK_HOURS = 48;
const MOST_RECHECKS = 5;

/**
 * The resident's own orders the app should ask Chapa about when it opens:
 * started, and recent enough that a payment could still be behind them,
 * newest first. A resident who paid and came back to the app by hand, rather
 * than through Chapa's return link, is confirmed this way. Asking settles
 * nothing by itself; the function only records what Chapa says.
 */
export function chapaOrdersToRecheck(orders: readonly ChapaOrder[], now: Date): string[] {
  const since = now.getTime() - CHAPA_RECHECK_HOURS * 60 * 60 * 1000;
  return orders
    .filter((order) => order.status === "started" && (order.createdAt?.getTime() ?? 0) >= since)
    .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0))
    .slice(0, MOST_RECHECKS)
    .map((order) => order.id);
}

/**
 * Whether Chapa is offered anywhere. While this is false no complex offers
 * Chapa whatever its own switch says, and the admin console leaves the Chapa
 * card out. Its functions must be deployed before an app that has it on.
 */
export const CHAPA_LAUNCHED = true;

/**
 * Whether a resident should see the Chapa button: Chapa has launched, and the
 * committee's subaccount is saved and switched on. No plan gates it, because
 * Nadawi's share comes from Chapa's commission on each payment rather than
 * from the plan.
 */
export function offersChapa(organization: Record<string, unknown> | undefined): boolean {
  if (!CHAPA_LAUNCHED) return false;
  const capabilities = organization?.capabilities as { chapa?: unknown } | undefined;
  return capabilities?.chapa === true;
}

/* ------------------------------------------------------ the committee's money */

/**
 * How long Chapa takes to pay a committee's subaccount out to its bank. Chapa
 * gives about a day, so a payment younger than this is shown as on its way and
 * an older one as reached the bank. It is a promise of Chapa's rather than
 * something Chapa reports per payment, and the screens word it as "about".
 */
export const CHAPA_SETTLEMENT_HOURS = 24;

const HOUR = 60 * 60 * 1000;

/** A payment Chapa confirmed, as the accountant's and the committee's lists show it. */
export interface ChapaReceipt {
  paymentId: string;
  unitId: string;
  amount: number;
  /** Chapa's reference for the payment. */
  reference: string;
  /** When Chapa confirmed it. */
  paidAt: Date | null;
  status: "submitted" | "verified" | "rejected";
}

/**
 * One Chapa payment's receipt, as the accountant opens it: what Chapa says
 * about the payment, asked at that moment, beside Liyu's own record of it.
 * Chapa v2 hosts no receipt page; its guidance is to build the receipt from
 * the verified payment, which is what this is.
 */
export interface ChapaReceiptDetail {
  paymentId: string;
  /** Liyu's reference for the payment, which Chapa calls the merchant reference. */
  orderId: string;
  unitId: string;
  block: string | null;
  /** Whole birr, as Liyu recorded it. */
  amount: number;
  status: ChapaReceipt["status"];
  /** The months it paid, oldest first. */
  settledPeriods: string[];
  /** What it paid beyond what was owed, waiting on the accountant. */
  heldAmount: number;
  chapa: {
    status: string | null;
    reference: string | null;
    /** Whole birr, or null when Chapa reports none or a part of a birr. */
    amount: number | null;
    currency: string | null;
    method: string | null;
    /** ISO time. */
    paidAt: string | null;
    payerName: string | null;
    payerPhone: string | null;
    /** Chapa reports a success, in birr, for exactly the amount Liyu recorded. */
    matches: boolean;
  };
  /** ISO time Chapa was asked. */
  checkedAt: string;
}

export interface ChapaMoney {
  /** Paid in the last day, so still on its way to the committee's bank. */
  onTheWay: number;
  onTheWayCount: number;
  /** Paid longer ago than that, so it should be in the committee's bank. */
  reached: number;
  reachedCount: number;
  total: number;
  /** When the newest payment on its way should reach the bank. */
  lastArrival: Date | null;
}

/** When a payment Chapa confirmed should be in the committee's bank. */
export function reachesBankBy(paidAt: Date): Date {
  return new Date(paidAt.getTime() + CHAPA_SETTLEMENT_HOURS * HOUR);
}

/**
 * What came in through Chapa, split into on its way and reached the bank.
 *
 * Every Chapa payment counts, whatever the accountant later decides about it:
 * Chapa has already split the money to the committee, so a payment held for
 * review is still money in the committee's account. A payment with no time on
 * it is counted as on its way, which is the cautious reading.
 */
export function chapaMoney(receipts: readonly ChapaReceipt[], now: Date): ChapaMoney {
  const money: ChapaMoney = {
    onTheWay: 0,
    onTheWayCount: 0,
    reached: 0,
    reachedCount: 0,
    total: 0,
    lastArrival: null,
  };
  for (const receipt of receipts) {
    assertBirr(receipt.amount, `Chapa payment ${receipt.paymentId}`);
    money.total += receipt.amount;
    const arrival = receipt.paidAt ? reachesBankBy(receipt.paidAt) : null;
    if (arrival && arrival.getTime() <= now.getTime()) {
      money.reached += receipt.amount;
      money.reachedCount += 1;
    } else {
      money.onTheWay += receipt.amount;
      money.onTheWayCount += 1;
      if (arrival && (!money.lastArrival || arrival > money.lastArrival)) {
        money.lastArrival = arrival;
      }
    }
  }
  return money;
}

/** A payment document, as a Chapa receipt, or null for any other kind of payment. */
export function toChapaReceipt(id: string, data: Record<string, unknown>): ChapaReceipt | null {
  if (data.method !== "chapa") return null;
  const paid = data.paidAt as { toDate?: () => Date } | Date | undefined;
  const status =
    data.status === "verified" || data.status === "rejected" ? data.status : "submitted";
  return {
    paymentId: id,
    unitId: typeof data.unitId === "string" ? data.unitId : "",
    amount: typeof data.amount === "number" ? data.amount : 0,
    reference: typeof data.reference === "string" ? data.reference : "",
    paidAt: paid instanceof Date ? paid : typeof paid?.toDate === "function" ? paid.toDate() : null,
    status,
  };
}
